import React, {useMemo, useState} from 'react';
import {IconButton, styled} from '@mui/material';
import {designSystemToken, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  CREATOR_ACTION_KEYS,
  CreatorActionKey,
} from '@/views/creators/components/creator-action/CreatorAction';
import useOffPlatformInviteDisabledReason from '@/views/creators/creator-crm/hooks/use-off-platform-invite-disabled-reason';
import Dropdown, {DropdownOption} from '@/components/dropdown/Dropdown';
import useOffPlatformCreatorQuery from '@/hooks/queries/use-off-platform-creator-query';
import useBrandId from '@/hooks/use-brand-id';

interface CreatorActionsMenuProps {
  creatorId: string;
  onCreatorAction: (creatorId: string, actionKey: CreatorActionKey) => void;
  disabledActionKeys?: Set<CreatorActionKey>;
  isOffPlatform?: boolean;
}

const EMPTY_SELECTED_IDS: string[] = [];

const StyledIconButton = styled(IconButton)({
  height: '30px',
  width: '30px',
});

const StyledIcon = styled(Icon)({
  transform: 'scale(1.5)',
});

const getOptions = (): DropdownOption[] => [
  {
    id: CREATOR_ACTION_KEYS.CREATE_AND_SHARE_LIST,
    label: translate('Share'),
    startIcon: <Icon name="Actions-Share" size="medium" appearance="neutral" />,
  },
  {
    id: CREATOR_ACTION_KEYS.ADD_TO_CAMPAIGN,
    label: translate('Invite to campaign'),
    startIcon: (
      <Icon name="Navigation-Megaphone" size="medium" appearance="neutral" />
    ),
  },
  {
    id: CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS,
    label: translate('Add to list'),
    startIcon: (
      <Icon name="Actions-Bookmark" size="medium" appearance="neutral" />
    ),
  },
  {
    id: CREATOR_ACTION_KEYS.SEND_EMAIL,
    label: translate('Message creators', {count: 1}),
    startIcon: (
      <Icon name="Navigation-Mail" size="medium" appearance="neutral" />
    ),
  },
  {
    id: CREATOR_ACTION_KEYS.ADD_NOTE,
    label: translate('Add note'),
    startIcon: (
      <Icon name="Features-Subtitles" size="medium" appearance="neutral" />
    ),
  },
];

function CreatorActionsMenu({
  creatorId,
  onCreatorAction,
  disabledActionKeys,
  isOffPlatform = false,
}: CreatorActionsMenuProps) {
  const [hasOpened, setHasOpened] = useState(false);
  const brandId = useBrandId();
  const shouldLoadInviteState = isOffPlatform && hasOpened;
  const {data: offPlatformCreator, isLoading: isCreatorLoading} =
    useOffPlatformCreatorQuery({
      id: creatorId,
      brandId,
      enabled: shouldLoadInviteState,
    });
  const {disabledReason: inviteDisabledReason, isLoading: isCampaignsLoading} =
    useOffPlatformInviteDisabledReason(offPlatformCreator, {
      enabled: shouldLoadInviteState,
    });
  const isInviteStateLoading =
    shouldLoadInviteState && (isCreatorLoading || isCampaignsLoading);

  const handleChange = (option: DropdownOption) => {
    onCreatorAction(creatorId, option.id as CreatorActionKey);
  };

  const options = useMemo(
    () =>
      getOptions()
        .filter((opt) => !disabledActionKeys?.has(opt.id as CreatorActionKey))
        .map((opt) =>
          isOffPlatform && opt.id === CREATOR_ACTION_KEYS.ADD_TO_CAMPAIGN
            ? {
                ...opt,
                disabled: isInviteStateLoading || !!inviteDisabledReason,
                subtitle: inviteDisabledReason ?? undefined,
              }
            : opt
        ),
    [
      disabledActionKeys,
      isOffPlatform,
      isInviteStateLoading,
      inviteDisabledReason,
    ]
  );

  if (options.length === 0) {
    return null;
  }

  return (
    <Dropdown
      label=""
      options={options}
      selectedIds={EMPTY_SELECTED_IDS}
      onChange={handleChange}
      selectionMode="single"
      menuWidth={296}
      showItemDividers={false}
      menuPositionHorizontal="left"
      onOpen={() => setHasOpened(true)}
      renderTrigger={({onClick}) => (
        <StyledIconButton onClick={onClick}>
          <StyledIcon
            name="Actions-More-Vertical"
            appearance="neutral"
            size="large"
            color={designSystemToken('semantic.fg.tertiary')}
          />
        </StyledIconButton>
      )}
    />
  );
}

export default CreatorActionsMenu;
